type AnalyticsEvent = 
  | { name: 'log_start'; props: { source: 'text' | 'voice' | 'chatbot' } }
  | { name: 'log_parsed'; props: { confidence: number; suggested_category: string } }
  | { name: 'log_saved'; props: { ms_to_save: number; amount: number } }
  | { name: 'log_success'; props: { source: 'text' | 'voice' | 'chatbot' } }
  | { name: 'log_undo'; props: { action_type: 'save' | 'edit' | 'delete' } }
  | { name: 'edit_open' }
  | { name: 'edit_saved' }
  | { name: 'delete_confirmed' }
  | { name: 'onboarding_completed'; props: { budget: number; currency: string; dark: boolean; calm: boolean } }
  | { name: 'offline_pending_count'; props: { count: number } }
  | { name: 'xp_gained'; props: { amount: number; source: string } }
  | { name: 'level_up'; props: { new_level: number } }
  | { name: 'streak_updated'; props: { streak_days: number } }
  | { name: 'badge_earned'; props: { badge_id: string } }
  | { name: 'weekly_reflection_viewed' }
  | { name: 'badge_cta_clicked'; props: { badge_id: string; action: string } }
  | { name: 'grace_window_used'; props: { streak_days: number } }
  | { name: 'next_up_viewed'; props: { badge_ids: string[] } };

import { ANALYTICS_CONFIG } from './config';

// Plausible Analytics configuration
const PLAUSIBLE_DOMAIN = ANALYTICS_CONFIG.domain;
const PLAUSIBLE_SRC = ANALYTICS_CONFIG.src;
const PLAUSIBLE_API = ANALYTICS_CONFIG.api;

let isAnalyticsLoaded = false;

/**
 * Initialize Plausible Analytics
 * Only called when consent.analytics === true
 */
export function initAnalytics(): void {
  // Prevent double-loading
  if (isAnalyticsLoaded || typeof window === 'undefined') return;

  try {
    const script = document.createElement('script');
    script.defer = true;
    script.dataset.domain = PLAUSIBLE_DOMAIN;
    script.dataset.api = PLAUSIBLE_API;
    script.src = PLAUSIBLE_SRC;

    // Mark as loaded when script loads
    script.onload = () => {
      isAnalyticsLoaded = true;
      console.log('[Analytics] Plausible loaded');
    };

    document.head.appendChild(script);
  } catch (error) {
    console.error('[Analytics] Failed to load Plausible:', error);
  }
}

/**
 * Unload analytics (when user opts out)
 */
export function unloadAnalytics(): void {
  try {
    const scripts = document.querySelectorAll(`script[src*="plausible"]`);
    scripts.forEach(script => script.remove());
    isAnalyticsLoaded = false;
    console.log('[Analytics] Plausible unloaded');
  } catch (error) {
    console.error('[Analytics] Error unloading analytics:', error);
  }
}

/**
 * Track custom events
 * Automatically checks consent before firing
 */
export const trackEvent = (event: AnalyticsEvent) => {
  // Always check consent first
  import('./consent').then(({ consentManager }) => {
    if (!consentManager.hasConsented('analytics')) {
      console.log('[Analytics] Event blocked (no consent):', event.name);
      return;
    }

    // Log to console for debugging
    console.log('[Analytics]', event.name, 'props' in event ? event.props : {});

    // Send to Plausible if available
    if (typeof window !== 'undefined' && (window as any).plausible) {
      try {
        const eventName = event.name;
        const props = 'props' in event ? event.props : undefined;
        
        if (props) {
          (window as any).plausible(eventName, { props });
        } else {
          (window as any).plausible(eventName);
        }
      } catch (error) {
        console.error('[Analytics] Plausible error:', error);
      }
    }
  });
};
